// src/pages/couriers/BusinessCourierManagementPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import {
  Truck,
  Plus,
  Settings,
  Trash2,
  Check,
  X,
  Search,
  Phone,
  Mail,
  Globe,
  MapPin,
  AlertCircle,
  RefreshCw,
  Key,
  Hash,
  Package,
  Map,
  Banknote
} from 'lucide-react';
import { 
  getBusinessCouriers, 
  assignCouriersToBusiness, 
  updateBusinessCourier, 
  removeBusinessCourier,
  enableWebhook,
  disableWebhook,
  regenerateWebhookSecret
} from '../../services/api/businessCourier';
import { getAllCourierCompanies } from '../../services/api/courierCompany';
import { usePermissions } from '../../hooks/usePermissions';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate } from '../../utils/dateUtils';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import AssignModal from './components/AssignModal';
import ConfigModal from './components/ConfigModal';
import CourierDeliveryChargesModal from './components/CourierDeliveryChargesModal';
import ColumnMappingsModal from './components/ColumnMappingsModal';
import DeleteModal from './components/DeleteModal';
import styles from './BusinessCourierManagementPage.module.css';

const BusinessCourierManagementPage = () => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const { success, error: showError } = useAlert();

  // State management
  const [businessCouriers, setBusinessCouriers] = useState([]);
  const [availableCouriers, setAvailableCouriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showMappingsModal, setShowMappingsModal] = useState(false);
  const [deliveryChargesCourier, setDeliveryChargesCourier] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Selection states
  const [selectedCourierIds, setSelectedCourierIds] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [selectedCourierForMappings, setSelectedCourierForMappings] = useState(null);
  const [courierToDelete, setCourierToDelete] = useState(null);
  
  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Load data on mount
  useEffect(() => {
    loadBusinessCouriers();
  }, []);

  const loadBusinessCouriers = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);
      const data = await getBusinessCouriers();
      setBusinessCouriers(data.couriers || []);
    } catch (err) {
      console.error('Failed to load business couriers:', err);
      setLoadingError(err.response?.data?.message || t('couriers.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableCouriers = async () => {
    try {
      const data = await getAllCourierCompanies();
      setAvailableCouriers(data.courierCompanies || []);
    } catch (err) {
      console.error('Failed to load available couriers:', err);
      showError(t('couriers.loadAvailableError'));
    }
  };

  const handleOpenAssignModal = async () => {
    await loadAvailableCouriers();
    setSelectedCourierIds([]);
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedCourierIds([]);
  };

  const handleToggleCourierSelection = (courierId) => {
    setSelectedCourierIds(prev => 
      prev.includes(courierId) 
        ? prev.filter(id => id !== courierId)
        : [...prev, courierId]
    );
  };

  const handleAssignCouriers = async () => {
    if (selectedCourierIds.length === 0) {
      showError(t('couriers.selectAtLeastOne'));
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await assignCouriersToBusiness(selectedCourierIds);
      
      success(t('couriers.createSuccess', { count: result.totalCreated }));
      handleCloseAssignModal();
      await loadBusinessCouriers();
    } catch (err) {
      console.error('Failed to assign couriers:', err);
      showError(err.response?.data?.message || t('couriers.assignError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenConfigModal = (courier) => {
    setSelectedCourier(courier);
    setShowConfigModal(true);
  };

  const handleCloseConfigModal = () => {
    setShowConfigModal(false);
    setSelectedCourier(null);
  };

  const handleSaveConfiguration = async (updateData) => {
    if (!selectedCourier) return;

    if (Object.keys(updateData).length === 0) {
      showError(t('couriers.noChanges'));
      return;
    }

    try {
      setIsSubmitting(true);
      await updateBusinessCourier(selectedCourier.id, updateData);
      success(t('couriers.updateSuccess'));
      handleCloseConfigModal();
      await loadBusinessCouriers();
    } catch (err) {
      console.error('Failed to update courier configuration:', err);
      showError(err.response?.data?.message || t('couriers.updateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnableWebhook = async () => {
    if (!selectedCourier) return;

    try {
      const result = await enableWebhook(selectedCourier.id);
      success(t('couriers.webhookEnabledSuccess'));
      
      setSelectedCourier(prev => ({
        ...prev,
        webhookEnabled: result.webhookEnabled,
        webhookUrl: result.webhookUrl
      }));
      
      await loadBusinessCouriers();
    } catch (err) {
      console.error('Failed to enable webhook:', err);
      showError(err.response?.data?.message || t('couriers.webhookEnableError'));
      throw err;
    }
  };

  const handleDisableWebhook = async () => {
    if (!selectedCourier) return;

    try {
      const result = await disableWebhook(selectedCourier.id);
      success(t('couriers.webhookDisabledSuccess'));
      
      setSelectedCourier(prev => ({
        ...prev,
        webhookEnabled: result.webhookEnabled
      }));
      
      await loadBusinessCouriers();
    } catch (err) {
      console.error('Failed to disable webhook:', err);
      showError(err.response?.data?.message || t('couriers.webhookDisableError'));
      throw err;
    }
  };

  const handleRegenerateWebhook = async () => {
    if (!selectedCourier) return;

    try {
      const result = await regenerateWebhookSecret(selectedCourier.id);
      success(t('couriers.webhookRegeneratedSuccess'));
      
      setSelectedCourier(prev => ({
        ...prev,
        webhookUrl: result.webhookUrl
      }));
      
      await loadBusinessCouriers();
    } catch (err) {
      console.error('Failed to regenerate webhook:', err);
      showError(err.response?.data?.message || t('couriers.webhookRegenerateError'));
      throw err;
    }
  };

  const handleOpenMappingsModal = (courier) => {
    setSelectedCourierForMappings(courier);
    setShowMappingsModal(true);
  };

  const handleCloseMappingsModal = () => {
    setShowMappingsModal(false);
    setSelectedCourierForMappings(null);
  };

  const handleOpenDeleteModal = (courier) => {
    setCourierToDelete(courier);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCourierToDelete(null);
  };

  const handleDeleteCourier = async () => {
    if (!courierToDelete) return;

    try {
      setIsSubmitting(true);
      await removeBusinessCourier(courierToDelete.id);
      success(t('couriers.removeSuccess'));
      handleCloseDeleteModal();
      await loadBusinessCouriers();
    } catch (err) {
      console.error('Failed to remove courier:', err);
      showError(err.response?.data?.message || t('couriers.removeError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter couriers based on search
  const filteredCouriers = businessCouriers.filter(courier => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      courier.accountName?.toLowerCase().includes(searchLower) ||
      courier.CourierCompany?.name?.toLowerCase().includes(searchLower) ||
      courier.CourierCompany?.description?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className={styles.courierManagementPage}>
        <PageHeader
          title={t('couriers.pageTitle')}
          description={t('couriers.pageDescription')}
          icon={Truck}
        />

        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('couriers.loadingCouriers')}</h3>
          <p>{t('couriers.loadingMessage')}</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.courierManagementPage}>
        <PageHeader
          title={t('couriers.pageTitle')}
          description={t('couriers.pageDescription')}
          icon={Truck}
        />

        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('couriers.unableToLoad')}</h3>
          <p>{loadingError}</p>
          <button onClick={loadBusinessCouriers} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('couriers.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.courierManagementPage}>
      <PageHeader
        title={t('couriers.pageTitle')}
        description={t('couriers.pageDescription')}
        icon={Truck}
        actions={
          hasPermission('assign_business_couriers') && (
            <button onClick={handleOpenAssignModal} className={styles.primaryButton}>
              <Plus size={20} />
              {t('couriers.assignCourier')}
            </button>
          )
        }
      />

      {/* Controls */}
      <div className={styles.pageControls}>
        <div className={styles.searchInputWrapper}>
          <Search className={styles.searchIcon} size={20} />
          <input
            type="text"
            placeholder={t('couriers.searchCouriers')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className={styles.clearSearchButton}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('couriers.totalAssigned')}:</span>
            <span className={styles.statValue}>{businessCouriers.length}</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('couriers.active')}:</span>
            <span className={styles.statValue}>
              {businessCouriers.filter(c => c.isActive).length}
            </span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('couriers.apiEnabled')}:</span>
            <span className={styles.statValue}>
              {businessCouriers.filter(c => c.apiConfigEnabled).length}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.pageContent}>
        {filteredCouriers.length === 0 ? (
          <div className={styles.emptyState}>
            {searchTerm ? (
              <>
                <Search className={styles.emptyIcon} size={48} />
                <h3>{t('couriers.noCouriersFound')}</h3>
                <p>{t('couriers.noMatchingCouriers')}</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className={styles.secondaryButton}
                >
                  {t('couriers.clearSearch')}
                </button>
              </>
            ) : (
              <>
                <Truck className={styles.emptyIcon} size={48} />
                <h3>{t('couriers.noCouriersAssigned')}</h3>
                <p>{t('couriers.startByAssigning')}</p>
                {hasPermission('assign_business_couriers') && (
                  <button onClick={handleOpenAssignModal} className={styles.primaryButton}>
                    <Plus size={20} />
                    {t('couriers.assignFirstCourier')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className={styles.couriersGrid}>
            {filteredCouriers.map(courier => (
              <div key={courier.id} className={styles.courierCard}>
                <div className={styles.courierCardHeader}>
                  <div className={styles.courierNameSection}>
                    <h3>{courier.accountName}</h3>
                  </div>
                  
                  <div className={styles.courierActions}>
                    {hasPermission('assign_business_couriers') && (
                      <>
                        <button
                          onClick={() => handleOpenConfigModal(courier)}
                          className={styles.iconButton}
                          title={t('couriers.configure')}
                        >
                          <Settings size={16} />
                        </button>
                        <button
                          onClick={() => setDeliveryChargesCourier(courier)}
                          className={styles.iconButton}
                          title={t('couriers.deliveryCharges')}
                        >
                          <Banknote size={16} />
                        </button>
                        <button
                          onClick={() => handleOpenMappingsModal(courier)}
                          className={styles.iconButton}
                          title={t('couriers.mappings.openMappings')}
                        >
                          <Map size={16} />
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(courier)}
                          className={`${styles.iconButton} ${styles.dangerIcon}`}
                          title={t('couriers.remove')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>

                  <div className={styles.courierBadges}>
                    <span className={`${styles.badge} ${courier.isActive ? styles.activeBadge : styles.inactiveBadge}`}>
                      {courier.isActive ? (
                        <>
                          <Check size={12} />
                          {t('couriers.activeStatus')}
                        </>
                      ) : (
                        <>
                          <X size={12} />
                          {t('couriers.inactiveStatus')}
                        </>
                      )}
                    </span>
                    {courier.apiConfigEnabled && (
                      <span className={`${styles.badge} ${styles.apiBadge}`}>
                        <Key size={12} />
                        {t('couriers.apiEnabledBadge')}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.courierCardBody}>
                  {courier.CourierCompany?.description && (
                    <p className={styles.courierDescription}>
                      {courier.CourierCompany.description}
                    </p>
                  )}

                  <div className={styles.courierDetails}>
                    {courier.CourierCompany?.contactInfo?.phone && (
                      <div className={styles.detailItem}>
                        <Phone size={14} />
                        <span>{courier.CourierCompany.contactInfo.phone}</span>
                      </div>
                    )}
                    {courier.CourierCompany?.contactInfo?.email && (
                      <div className={styles.detailItem}>
                        <Mail size={14} />
                        <span>{courier.CourierCompany.contactInfo.email}</span>
                      </div>
                    )}
                    {courier.CourierCompany?.contactInfo?.website && (
                      <div className={styles.detailItem}>
                        <Globe size={14} />
                        <span>{courier.CourierCompany.contactInfo.website}</span>
                      </div>
                    )}
                    {courier.CourierCompany?.contactInfo?.address && (
                      <div className={styles.detailItem}>
                        <MapPin size={14} />
                        <span>{courier.CourierCompany.contactInfo.address}</span>
                      </div>
                    )}
                  </div>

                  {(courier.waybillRangeStart || courier.webhookEnabled) && (
                    <div className={styles.configInfo}>
                      {courier.waybillRangeStart && (
                        <div className={styles.configItem}>
                          <Hash size={12} />
                          <span>{t('couriers.waybillRange')}: {courier.waybillRangeStart} - {courier.waybillRangeEnd}</span>
                        </div>
                      )}
                      {courier.lastWaybillUsed && (
                        <div className={styles.configItem}>
                          <Package size={12} />
                          <span>{t('couriers.lastUsed')}: {courier.lastWaybillUsed}</span>
                        </div>
                      )}
                      {courier.webhookEnabled && (
                        <div className={styles.configItem}>
                          <Check size={12} />
                          <span>{t('couriers.webhookEnabledBadge')}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className={styles.courierCardFooter}>
                  <span className={styles.createdDate}>
                    {t('couriers.addedOn')} {formatDate(courier.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AssignModal
        isOpen={showAssignModal}
        onClose={handleCloseAssignModal}
        availableCouriers={availableCouriers}
        selectedCourierIds={selectedCourierIds}
        onToggleSelection={handleToggleCourierSelection}
        onAssign={handleAssignCouriers}
        isSubmitting={isSubmitting}
      />

      <ConfigModal
        isOpen={showConfigModal}
        onClose={handleCloseConfigModal}
        courier={selectedCourier}
        onSave={handleSaveConfiguration}
        isSubmitting={isSubmitting}
        onEnableWebhook={handleEnableWebhook}
        onDisableWebhook={handleDisableWebhook}
        onRegenerateWebhook={handleRegenerateWebhook}
      />

      <CourierDeliveryChargesModal
        isOpen={!!deliveryChargesCourier}
        onClose={() => setDeliveryChargesCourier(null)}
        courier={deliveryChargesCourier}
      />

      <ColumnMappingsModal
        isOpen={showMappingsModal}
        onClose={handleCloseMappingsModal}
        courier={selectedCourierForMappings}
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        courier={courierToDelete}
        onConfirm={handleDeleteCourier}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default BusinessCourierManagementPage;